<script setup>
import { RouterLink, useRouter } from 'vue-router'
import { useModalStack } from '@/stores/modalstack'
import { storeToRefs } from 'pinia'
import { computed, inject, nextTick } from 'vue'
import { saved as savedState } from '@/composables/ui'
import { useSessionStorage } from '@vueuse/core'
import { merge } from 'lodash'
import { useRoute } from 'vue-router'
const currentRoute = useRoute()

const modalStack = useModalStack()
const { stack } = storeToRefs(modalStack)
const backlinkSession = useSessionStorage('backlink')
const { push: openModal } = modalStack

const props = defineProps({
  ...RouterLink.props,
  to: [String, Object],
  backlink: Boolean,
  replace: Boolean,
  historyBack: {
    type: Boolean,
    default: true,
  },
  state: [null, Object],
  save: {
    type: [Boolean, String],
    default: false,
  },
  modal: Boolean,
})
defineOptions({ inheritAttrs: false })
const router = useRouter()

const _inmodal = inject('modalstack', false)
const inmodal = computed(() => props.modal || _inmodal)
const safeProps = computed(() => {
  const p = { ...props }
  if (p.to === undefined) delete p.to
  return p
})

async function onNavigate(navigate, route) {
  // navigate via modalstack
  if (inmodal.value) {
    const cm = route.matched.filter(r => r.components)
    if (props.backlink) {
      modalStack.pop()
    } else {
      openModal({
        component: cm[0].components.default,
        props: {
          ...route.params,
        },
      })
    }

    return
  }

  if (props.backlink) {
    const bl = backlinkSession.value

    if (bl) {
      backlinkSession.value = undefined
      if (props.replace) {
        router.replace(bl)
      } else {
        router.push(bl)
      }
      return
    }

    if (props.historyBack === true && router.options.history.state.back) {
      router.back()
      return
    }
  }

  if (props.save) {
    let restoreObj = savedState.value?.[route.fullPath]

    if (!restoreObj) {
      restoreObj = {}
    }

    merge(restoreObj, { [props.save]: props.state })

    savedState.value = {
      ...savedState.value,
      [route.fullPath]: restoreObj,
    }

    if (props.backlink) {
      backlinkSession.value = route.fullPath
    }

    await nextTick()
  }

  stack.value = []
  navigate()
}
</script>
<template>
  <RouterLink
    v-if="props.to && !props.backlink"
    v-bind="safeProps"
    custom
    #="{ href, navigate, route }"
  >
    <a
      v-bind="$attrs"
      :href="href"
      @click.prevent.stop="onNavigate(navigate, route)"
    >
      <slot />
    </a>
  </RouterLink>

  <a
    v-else
    v-bind="$attrs"
    href="#"
    @click.prevent.stop="onNavigate(() => {}, currentRoute)"
  >
    <slot />
  </a>
</template>
